import { useState, useEffect, useCallback } from 'react';
import { analyticsUtility } from '../utils/analyticsUtility';
import { DashboardBatch, DashboardAnalyticsFilters, ChartDataPoint } from '../types';

interface AnalyticsState {
  // Data
  batches: DashboardBatch[];
  chartData: ChartDataPoint[];
  overallStats: {
    totalRevenue: number;
    totalMembers: number;
    averageRevenuePerMember: number;
    memberGrowthRate: number;
    revenueGrowthRate: number;
    activityBreakdown: {
      contributions: number;
      benefits: number;
      events: number;
      expenses: number;
    };
    percentiles?: {
      revenue_50th: number;
      revenue_90th: number;
      revenue_95th: number;
      revenue_99th: number;
    };
  };
  isLoading: boolean;
  error: string | null;

  // Filters
  filters: DashboardAnalyticsFilters;

  // Real-time
  isRealTimeEnabled: boolean;
  refreshInterval: number;

  // Chart interactions
  selectedMetric: string;
  selectedTimeRange: string;

  // Drill-down
  drillDownData: unknown;
  isDrillDownActive: boolean;

  // Performance monitoring
  performanceMetrics: {
    loadTime: number;
    renderTime: number;
    dataPoints: number;
  };

  // Methods
  setFilters: (filters: DashboardAnalyticsFilters) => void;
  setIsRealTimeEnabled: (enabled: boolean) => void;
  setRefreshInterval: (interval: number) => void;
  setSelectedMetric: (metric: string) => void;
  setSelectedTimeRange: (range: string) => void;
  setDrillDownData: (data: unknown) => void;
  setIsDrillDownActive: (active: boolean) => void;
  exportDashboard: (format: 'pdf' | 'excel' | 'png') => Promise<void>;
  refreshData: () => void;
}

export const useAnalyticsUtility = (): AnalyticsState => {
  const [state, setState] = useState(() => analyticsUtility.getState());

  useEffect(() => {
    const unsubscribe = analyticsUtility.subscribe((newState) => {
      setState(newState);
    });

    // Fetch data now that the user is authenticated (hook only mounts post-login)
    analyticsUtility.init();

    return unsubscribe;
  }, []);

  const setFilters = useCallback((filters: DashboardAnalyticsFilters) => {
    analyticsUtility.setFilters(filters);
  }, []);

  const setIsRealTimeEnabled = useCallback((enabled: boolean) => {
    analyticsUtility.setIsRealTimeEnabled(enabled);
  }, []);

  const setRefreshInterval = useCallback((interval: number) => {
    analyticsUtility.setRefreshInterval(interval);
  }, []);

  const setSelectedMetric = useCallback((metric: string) => {
    analyticsUtility.setSelectedMetric(metric);
  }, []);

  const setSelectedTimeRange = useCallback((range: string) => {
    analyticsUtility.setSelectedTimeRange(range);
  }, []);

  const setDrillDownData = useCallback((data: unknown) => {
    analyticsUtility.setDrillDownData(data);
  }, []);

  const setIsDrillDownActive = useCallback((active: boolean) => {
    analyticsUtility.setIsDrillDownActive(active);
  }, []);

  const exportDashboard = useCallback(async (format: 'pdf' | 'excel' | 'png') => {
    await analyticsUtility.exportDashboard(format);
  }, []);

  const refreshData = useCallback(() => {
    analyticsUtility.refreshData();
  }, []);

  return {
    ...state,
    setFilters,
    setIsRealTimeEnabled,
    setRefreshInterval,
    setSelectedMetric,
    setSelectedTimeRange,
    setDrillDownData,
    setIsDrillDownActive,
    exportDashboard,
    refreshData
  };
};